<script setup lang="ts">
  import { useInfluencersStore } from '@/store/influencers';
  import { formatNumber } from '@/utils/strings';
  import { computed, onMounted, ref } from 'vue';

  const influencerStore = useInfluencersStore();

  interface ProfileMetric {
    icon: string;
    value: string | number;
    label: string;
    color?: string;
  }

  interface Influencer {
    image: string | null;
    influencer_id: number;
    followers: number;
    name: string;
    date: string;
    impressions: number;
    media_type: string;
    reach: number;
    saved: number;
    shares: number;
    submission_link: string;
    total_interactions: number;
    platform: string;
    cost: number;
    engagement_rate: number;
    er: string;
    cpm: number;
    cpe: number;
    cpr: number;
    occurrences: number;
  }

  interface Props {
    influencer?: Influencer;
  }

  const props = defineProps<Props>();

  const metricsData = computed<ProfileMetric[]>(() => {
    if (!props.influencer) return [];

    return [
      {
        icon: 'lucide:eye',
        value: formatNumber(props.influencer.impressions, { notation: 'compact' }),
        label: 'Impressions',
        color: '#3B82F6',
      },
      {
        icon: 'lucide:users',
        value: formatNumber(props.influencer.reach, { notation: 'compact' }),
        label: 'Reach',
        color: '#22D3EE',
      },
      {
        icon: 'lucide:download',
        value: formatNumber(Math.abs(props.influencer.shares), { notation: 'compact' }),
        label: 'Shares',
        color: '#06B6D4',
      },
      {
        icon: 'lucide:bookmark',
        value: formatNumber(Math.abs(props.influencer.saved), { notation: 'compact' }),
        label: 'Saved',
        color: '#F59E0B',
      },
      {
        icon: 'lucide:trending-up',
        value: `${formatNumber(props.influencer.engagement_rate, { notation: 'compact', fractionDigits: 1 })}%`,
        label: 'Engagement',
        color: '#10B981',
      },
    ];
  });

  const username = computed(() => {
    if (!props.influencer) return '';

    try {
      const { platform, submission_link } = props.influencer;

      if (platform.toLowerCase() === 'twitter') {
        const match = submission_link.match(/x\.com\/([^\/]+)/);
        return match ? match[1] : 'username';
      } else if (platform.toLowerCase() === 'instagram') {
        const match = submission_link.match(/instagram\.com\/([^\/]+)/);

        return '';
      } else if (platform.toLowerCase() === 'tiktok') {
        const match = submission_link.match(/tiktok\.com\/@([^\/]+)/);
        return match ? match[1] : 'tiktok';
      } else if (platform.toLowerCase() === 'facebook') {
        return 'facebook';
      }
    } catch (e) {
      return 'username';
    }
    return 'username';
  });

  const influencerAvatar = ref('');

  const profileImage = computed(() => {
    if (!props.influencer) return '';
    return (
      props.influencer.image ||
      influencerAvatar.value ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(props.influencer.name)}&background=random`
    );
  });

  const getInfluencerData = async () => {
    if (!props.influencer) return;
    const data = await influencerStore.doSelectInfluencersByIds([String(props.influencer.influencer_id)], true);
    influencerAvatar.value = data[0].avatar;
  };

  onMounted(async () => {
    await getInfluencerData();
  });
</script>

<template>
  <div v-if="!influencer" class="loading-state">
    <div class="loading-card">
      <div class="loading-spinner"></div>
      <p>Loading metrics...</p>
    </div>
  </div>

  <div v-else class="profile-card">
    <div class="profile-header">
      <img :src="profileImage" :alt="influencer.name" class="profile-avatar" />
      <div class="profile-info">
        <h2 class="profile-name">{{ influencer.name }}</h2>
        <p class="profile-username" v-if="username">@{{ username }}</p>
        <p class="profile-stats">
          {{ formatNumber(influencer.followers, { notation: 'compact' }) }} followers · {{ influencer.platform }} ·
          {{ influencer.media_type }}
        </p>
      </div>
      <a :href="influencer.submission_link" target="_blank" rel="noopener noreferrer" class="external-link-btn">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
        </svg>
      </a>
    </div>

    <div class="metrics-container">
      <div v-for="(metric, index) in metricsData" :key="index" class="metric-card" :class="metric.label.toLowerCase()">
        <div class="metric-icon" :style="{ color: metric.color }">
          <iconify-icon :icon="metric.icon" />
        </div>
        <div class="metric-value">{{ metric.value }}</div>
        <div class="metric-label">{{ metric.label }}</div>
      </div>

      <div class="metric-card cpe-card">
        <div class="cpe-value">${{ influencer.cpe.toFixed(2) }}</div>
        <div class="metric-label">CPE</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .loading-state {
    background: white;
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    max-width: 1400px;
    margin: 0 auto;
  }

  .loading-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: #64748b;
    font-size: 14px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f1f5f9;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .profile-card {
    background: white;
    border-radius: 24px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    max-width: 1400px;

    .profile-name {
      font-size: 14px;
      font-weight: 600;
      color: #2563eb;
      margin: 0 0 4px 0;

      @media screen and (min-width: 1024px) {
        font-size: 20px;
      }
    }
  }

  .profile-header {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 32px;
    position: relative;
    transition: all 0.3s ease;
    padding: 8px;
    border-radius: 12px;

    &:hover {
      background-color: #f1f5f980;
    }
  }

  .profile-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
  }

  .profile-info {
    flex: 1;
  }

  .profile-username {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 8px 0;
  }

  .profile-stats {
    font-size: 12px;
    color: #64748b;
    margin: 0;
  }

  .external-link-btn {
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 8px;
    transition: color 0.2s;
    text-decoration: none;
  }

  .external-link-btn:hover {
    color: #64748b;
  }

  .metrics-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    gap: 16px;
  }

  .metric-card {
    background: #f8fafc;
    border-radius: 16px;
    padding: 12px;
    text-align: center;
    transition:
      transform 0.2s,
      box-shadow 0.2s;

    &.impressions {
      background-color: #3b82f60d;
    }

    &.reach {
      background-color: #0ea5e90d;
    }

    &.shares {
      background-color: #14b8a60d;
    }

    &.saved {
      background-color: #f59e0b0d;
    }

    &.engagement {
      background-color: #22c55e0d;
    }
  }

  .metric-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .metric-icon {
    font-size: 16px;
    margin-bottom: 12px;
  }

  .metric-value {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
  }

  .metric-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
  }

  .cpe-card {
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .cpe-value {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
  }

  @media screen and (min-width: 768px) {
    .metrics-container {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media screen and (min-width: 1024px) {
    .metrics-container {
      grid-template-columns: repeat(6, 1fr);
    }
  }
</style>
